// src/pages/Alerts.js
import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Plus, Edit2, Trash2, RefreshCw, CheckCircle, AlertTriangle, Info, X, Save, ShieldAlert, Eye } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const SEV_CFG = {
  info:     { label: 'Info',    color: 'var(--accent)',   bg: '#4f8ef720', icon: Info },
  warning:  { label: 'Avertizare', color: 'var(--warning)', bg: '#f59e0b20', icon: AlertTriangle },
  critical: { label: 'Critic',  color: 'var(--danger)',   bg: '#ef444420', icon: ShieldAlert },
};

const STATUS_EVENT_CFG = {
  open:         { label: 'Deschis',       color: 'var(--danger)' },
  acknowledged: { label: 'Confirmat',     color: 'var(--warning)' },
  resolved:     { label: 'Rezolvat',      color: 'var(--success)' },
};

const MODULES = ['monitor','hosting','invoices','tickets','backups','docker','clients'];
const CONDITION_TYPES = {
  monitor:  ['target_down','target_up','uptime_below','response_above'],
  hosting:  ['domain_expiry_lt','ssl_expiry_lt','vps_expiry_lt'],
  invoices: ['invoice_overdue','invoice_created','payment_received'],
  tickets:  ['ticket_created','ticket_critical','ticket_overdue'],
  backups:  ['backup_failed','backup_success','backup_not_run'],
  docker:   ['container_stopped','container_error'],
  clients:  ['client_created'],
};

function RuleModal({ rule, onClose, onSaved }) {
  const isEdit = !!rule?.id;
  const [f, setF] = useState(isEdit ? {
    ...rule, channels: typeof rule.channels === 'string' ? JSON.parse(rule.channels || '[]') : (rule.channels || [])
  } : { name: '', module: 'monitor', condition_type: 'target_down', condition_value: '', severity: 'warning', channels: ['email'], cooldown_minutes: 60, is_active: true });
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const toggleChannel = (ch) => {
    setF(p => ({
      ...p,
      channels: p.channels.includes(ch) ? p.channels.filter(c => c !== ch) : [...p.channels, ch]
    }));
  };

  const save = async () => {
    if (!f.name) return toast.error('Numele este obligatoriu');
    setLoading(true);
    try {
      if (isEdit) { await api.put(`/alerts/rules/${rule.id}`, f); toast.success('Regulă actualizată'); }
      else { await api.post('/alerts/rules', f); toast.success('Regulă creată'); }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  const conditions = CONDITION_TYPES[f.module] || [];

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-xl">
        <div className="modal-header">
          <span className="modal-title"><Bell size={16} /> {isEdit ? rule.name : 'Regulă Alertă Nouă'}</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-grid form-grid-2">
            <div className="form-group"><label className="form-label">Nume *</label><input className="form-input" value={f.name} onChange={e => set('name', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Severitate</label>
              <select className="form-select" value={f.severity} onChange={e => set('severity', e.target.value)}>
                {Object.entries(SEV_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Modul</label>
              <select className="form-select" value={f.module} onChange={e => { set('module', e.target.value); set('condition_type', CONDITION_TYPES[e.target.value]?.[0] || ''); }}>
                {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Condiție</label>
              <select className="form-select" value={f.condition_type} onChange={e => set('condition_type', e.target.value)}>
                {conditions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Valoare condiție (opțional)</label><input className="form-input" value={f.condition_value || ''} onChange={e => set('condition_value', e.target.value)} placeholder="Ex: 7 (zile), 99 (%), 500 (ms)" /></div>
            <div className="form-group"><label className="form-label">Cooldown (minute)</label><input className="form-input" type="number" value={f.cooldown_minutes} onChange={e => set('cooldown_minutes', e.target.value)} /></div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Canale notificare</label>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                {['email', 'sms', 'webhook', 'slack', 'telegram'].map(ch => (
                  <label key={ch} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, padding: '4px 10px', borderRadius: 8, background: f.channels?.includes(ch) ? 'var(--accent)20' : 'var(--bg-hover)', border: `1px solid ${f.channels?.includes(ch) ? 'var(--accent)' : 'var(--border)'}` }}>
                    <input type="checkbox" checked={!!f.channels?.includes(ch)} onChange={() => toggleChannel(ch)} style={{ display: 'none' }} />
                    {ch}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, marginTop: 8 }}>
            <input type="checkbox" checked={!!f.is_active} onChange={e => set('is_active', e.target.checked)} />
            Regulă activă
          </label>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Anulare</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <span className="spinner" /> : <><Save size={14} /> {isEdit ? 'Actualizare' : 'Creare'}</>}</button>
        </div>
      </div>
    </div>
  );
}

function EventDetailModal({ event, onClose, onAction }) {
  const toast = useToast();

  const acknowledge = async () => {
    try { await api.post(`/alerts/events/${event.id}/acknowledge`); toast.success('Confirmat'); onAction(); onClose(); }
    catch { toast.error('Eroare'); }
  };

  const resolve = async () => {
    try { await api.post(`/alerts/events/${event.id}/resolve`); toast.success('Rezolvat'); onAction(); onClose(); }
    catch { toast.error('Eroare'); }
  };

  const sevCfg = SEV_CFG[event.severity] || SEV_CFG.warning;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <span className="modal-title" style={{ color: sevCfg.color }}><sevCfg.icon size={16} /> {event.title}</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 10, color: sevCfg.color, background: sevCfg.bg }}>{sevCfg.label}</span>
            <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 10, color: STATUS_EVENT_CFG[event.status]?.color || 'var(--text-secondary)', background: 'var(--bg-hover)' }}>
              {STATUS_EVENT_CFG[event.status]?.label || event.status}
            </span>
          </div>
          {event.message && <div style={{ background: 'var(--bg-hover)', borderRadius: 8, padding: '10px 14px', fontSize: 14, lineHeight: 1.6, marginBottom: 12 }}>{event.message}</div>}
          {[
            ['Regulă', event.rule_name || '—'],
            ['Creat', new Date(event.created_at).toLocaleString('ro-RO')],
            event.acknowledged_by_name && ['Confirmat de', event.acknowledged_by_name],
            event.acknowledged_at && ['La', new Date(event.acknowledged_at).toLocaleString('ro-RO')],
          ].filter(Boolean).map(([label, val]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)33', fontSize: 13 }}>
              <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
              <span style={{ fontWeight: 600 }}>{val}</span>
            </div>
          ))}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Închidere</button>
          {event.status === 'open' && <button className="btn btn-secondary" onClick={acknowledge}><Eye size={14} /> Confirmă</button>}
          {event.status !== 'resolved' && <button className="btn btn-primary" onClick={resolve}><CheckCircle size={14} /> Rezolvă</button>}
        </div>
      </div>
    </div>
  );
}

export default function Alerts() {
  const [rules, setRules] = useState([]);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('events');
  const [filterSev, setFilterSev] = useState('');
  const [filterStatus, setFilterStatus] = useState('open');
  const [modal, setModal] = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);
  const toast = useToast();
  const { hasRole } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, e, s] = await Promise.all([
        api.get('/alerts/rules'),
        api.get('/alerts/events', { params: { status: filterStatus || undefined, severity: filterSev || undefined, limit: 100 } }),
        api.get('/alerts/stats'),
      ]);
      setRules(r.data); setEvents(e.data); setStats(s.data);
    } catch (e) { toast.error('Eroare la încărcare'); }
    finally { setLoading(false); }
  }, [filterStatus, filterSev]);

  useEffect(() => { load(); }, [load]);

  const delRule = async (id, name) => {
    if (!window.confirm(`Ștergi regula "${name}"?`)) return;
    try { await api.delete(`/alerts/rules/${id}`); toast.success('Regulă ștearsă'); load(); }
    catch { toast.error('Eroare'); }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><div className="page-title">Alerte & Notificări</div><div className="page-subtitle">Reguli de alertare și monitorizare evenimente</div></div>
        <button className="btn btn-primary" onClick={() => setModal({ new: true })}><Plus size={14} /> Regulă Nouă</button>
      </div>

      {stats && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          {[
            { label: 'Reguli Active', value: stats.rules?.active ?? 0, color: 'var(--success)', icon: Bell },
            { label: 'Total Reguli', value: stats.rules?.total ?? 0, color: 'var(--accent)', icon: Bell },
            { label: 'Alerte Deschise', value: stats.events?.open ?? 0, color: 'var(--danger)', icon: AlertTriangle },
            { label: 'Critice Deschise', value: stats.events?.critical ?? 0, color: 'var(--danger)', icon: ShieldAlert },
          ].map(k => (
            <div key={k.label} className="stat-card">
              <div className="stat-icon" style={{ background: `${k.color}20`, marginBottom: 10 }}><k.icon size={16} color={k.color} /></div>
              <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
          {[
            { id: 'events', label: `Evenimente (${events.length})`, icon: AlertTriangle },
            { id: 'rules', label: `Reguli (${rules.length})`, icon: Bell },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === t.id ? 700 : 400, color: activeTab === t.id ? 'var(--accent)' : 'var(--text-secondary)', borderBottom: activeTab === t.id ? '2px solid var(--accent)' : '2px solid transparent' }}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'events' && (
          <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <select className="form-select" style={{ width: 150 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">Toate statusurile</option>
                {Object.entries(STATUS_EVENT_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select className="form-select" style={{ width: 150 }} value={filterSev} onChange={e => setFilterSev(e.target.value)}>
                <option value="">Toate severitățile</option>
                {Object.entries(SEV_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} /></button>
            </div>
            {loading ? <div className="loading-center"><span className="spinner" /></div> : events.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--success)' }}>
                <CheckCircle size={36} style={{ display: 'block', margin: '0 auto 12px' }} />
                <div style={{ fontWeight: 600 }}>Niciun eveniment deschis 🎉</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {events.map(ev => {
                  const sevCfg = SEV_CFG[ev.severity] || SEV_CFG.warning;
                  const stCfg = STATUS_EVENT_CFG[ev.status] || STATUS_EVENT_CFG.open;
                  return (
                    <div key={ev.id} onClick={() => setDetailEvent(ev)} style={{ display: 'flex', gap: 12, padding: '12px 14px', background: 'var(--bg-hover)', borderRadius: 10, cursor: 'pointer', border: `1px solid ${ev.severity === 'critical' ? '#ef444433' : ev.severity === 'warning' ? '#f59e0b22' : 'var(--border)'}`, alignItems: 'center' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-hover)'}>
                      <sevCfg.icon size={18} color={sevCfg.color} style={{ flexShrink: 0 }} />
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{ev.title}</div>
                        {ev.message && <div style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.message}</div>}
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                          {ev.rule_name && <span>{ev.rule_name} · </span>}
                          {new Date(ev.created_at).toLocaleString('ro-RO')}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 8, color: sevCfg.color, background: sevCfg.bg }}>{sevCfg.label}</span>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 8, color: stCfg.color, background: 'var(--bg-card)' }}>{stCfg.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeTab === 'rules' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
              <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} /></button>
            </div>
            {loading ? <div className="loading-center"><span className="spinner" /></div> : (
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>NUME</th><th>MODUL</th><th>CONDIȚIE</th><th>SEVERITATE</th><th>CANALE</th><th>COOLDOWN</th><th>ACTIVĂ</th><th>ACȚIUNI</th></tr></thead>
                  <tbody>
                    {rules.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>Nicio regulă</td></tr>}
                    {rules.map(r => {
                      const sevCfg = SEV_CFG[r.severity] || SEV_CFG.warning;
                      const channels = typeof r.channels === 'string' ? JSON.parse(r.channels || '[]') : (r.channels || []);
                      return (
                        <tr key={r.id}>
                          <td style={{ fontWeight: 600 }}>{r.name}</td>
                          <td><span className="badge badge-neutral">{r.module}</span></td>
                          <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)' }}>
                            {r.condition_type}{r.condition_value ? ` < ${r.condition_value}` : ''}
                          </td>
                          <td><span style={{ fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 10, color: sevCfg.color, background: sevCfg.bg }}>{sevCfg.label}</span></td>
                          <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{channels.join(', ') || '—'}</td>
                          <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.cooldown_minutes}m</td>
                          <td>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: r.is_active ? 'var(--success)' : 'var(--text-secondary)', display: 'inline-block' }} />
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button className="btn-icon" onClick={() => setModal(r)}><Edit2 size={13} /></button>
                              {hasRole('superadmin', 'admin') && <button className="btn-icon" style={{ color: 'var(--danger)' }} onClick={() => delRule(r.id, r.name)}><Trash2 size={13} /></button>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {modal && <RuleModal rule={modal.new ? null : modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {detailEvent && <EventDetailModal event={detailEvent} onClose={() => setDetailEvent(null)} onAction={load} />}
    </div>
  );
}
